import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { Col, Tab } from 'react-bootstrap';
import { useContext, useEffect, useMemo, useState } from 'react';
import { getReport, getReportDetail } from '../../utils/API/reports';
import { UserContext } from '../../context/UserContext';
import { MonthNavHeader } from '../../components/Layout/MonthNavHeader';
import Kilometers from '../Kilometers';
import Expenses from '../Expenses';
import PanelTotal from '../../components/PanelTotal/panelTotal';
import ConfirmForm from '../confirmForm';

import { PERMISSIONS, ROUTES } from '../../constants';
import I3Modal from '../../components/Layout/I3Modal';
import { extractSummaryData, renderSpinner } from '../../utils/Utility';
import { MonthContext } from '../../context/MonthContext';
import { useKeyPress } from '../../hooks/useKeyPress';
import { MonoSelectUser } from '../../components/Form/MonoSelectUser';
import { EmployeeListContext } from '../../context/EmployeeListContext';
import { Unauthorized } from '../../components/Unauthorized';

const defaultModalVisibility = () => ({ trip: false, expense: false });

const getActiveModalKey = (pathname) => {
  if (pathname.includes(`/${ROUTES.KILOMETERS}`)) {
    return 'trip';
  }
  if (pathname.includes(`/${ROUTES.EXPENSES}`)) {
    return 'expense';
  }
  return null;
};

const Trips = () => {
  const { user, hasClaim } = useContext(UserContext);

  const { month } = useContext(MonthContext);
  const location = useLocation();
  const { employeesQueryA: users, employeesByIdQueryA: usersById } =
    useContext(EmployeeListContext);

  const [impersonatedUserId, setImpersonatedUserId] = useState(user.id);
  const selectedUserData = useMemo(
    () => usersById[impersonatedUserId] || user,
    [usersById, impersonatedUserId, user]
  );

  const selectedUser = selectedUserData?.username;

  const [report, setReport] = useState({});
  const [summary, setSummary] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(defaultModalVisibility());
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [showModalTableConfirm, setShowModalTableConfirm] = useState(
    defaultModalVisibility()
  );

  const selectedUserIsCurrentUser = useMemo(() => {
    if (!selectedUser) {
      return true;
    }
    return user?.username === selectedUser;
  }, [user, selectedUser]);

  const isReportConfirmed = report.confirmed || !report.id;

  const isReadOnly = isReportConfirmed || !selectedUserIsCurrentUser;

  const handleShowModalConfirm = () => setShowModalConfirm(true);
  const handleHideModalConfirm = () => setShowModalConfirm(false);

  const handleShowModal = () => {
    const active = getActiveModalKey(location.pathname);
    if (!active) {
      return;
    }
    setShowModal({
      trip: active === 'trip',
      expense: active === 'expense',
    });
  };

  const handleShowModalTableConfirm = () => {
    const active = getActiveModalKey(location.pathname);
    if (!active) {
      return;
    }
    setShowModalTableConfirm({
      trip: active === 'trip',
      expense: active === 'expense',
    });
  };

  const loadReport = async () => {
    setReport({});
    const reportData = await getReportDetail(
      month.format('YYYY'),
      month.format('MM'),
      selectedUser,
      'expenses'
    );
    setReport(reportData);
  };

  const updateSummary = async () => {
    if (!selectedUser) return;
    setSummary({});
    const reportData = await getReport(
      month.format('YYYY'),
      month.format('MM'),
      selectedUser
    );
    const summaryReportData = extractSummaryData(reportData);
    setSummary(summaryReportData);
  };

  useEffect(() => {
    if (!selectedUser) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    Promise.all([loadReport(), updateSummary()]).finally(() =>
      setIsLoading(false)
    );
  }, [month, selectedUser]);

  useKeyPress('F9', handleShowModal);
  useKeyPress('Escape', () => {
    setShowModal(defaultModalVisibility());
    setShowModalConfirm(false);
    setShowModalTableConfirm(defaultModalVisibility());
  });

  const canAccessTrips =
    hasClaim(PERMISSIONS.TRIPS_KILOMETERS_VIEW) ||
    hasClaim(PERMISSIONS.TRIPS_EXPENSES_VIEW) ||
    hasClaim(PERMISSIONS.TRIPS_KILOMETERS_USER_SELECTION) ||
    hasClaim(PERMISSIONS.TRIPS_EXPENSES_USER_SELECTION);

  if (!canAccessTrips) {
    return <Unauthorized />;
  }

  const isKilometersTab = location.pathname.includes(`/${ROUTES.KILOMETERS}`);
  const isExpensesTab = location.pathname.includes(`/${ROUTES.EXPENSES}`);
  const canEditCurrentTab =
    (isKilometersTab && hasClaim(PERMISSIONS.TRIPS_KILOMETERS_EDIT)) ||
    (isExpensesTab && hasClaim(PERMISSIONS.TRIPS_EXPENSES_EDIT));

  return (
    <>
      <div className="content__wrapper">
        <MonthNavHeader
          title="Nota spese e rimborso chilometrico"
          showInsertButton={!isReportConfirmed && selectedUserIsCurrentUser && canEditCurrentTab}
          onInsert={handleShowModal}
        >
          {(hasClaim(PERMISSIONS.TRIPS_KILOMETERS_USER_SELECTION) || hasClaim(PERMISSIONS.TRIPS_EXPENSES_USER_SELECTION)) && (
            <MonoSelectUser select={setImpersonatedUserId} users={users} />
          )}
        </MonthNavHeader>
        <div className="content__overflow">
          {isLoading ? (
            renderSpinner(true)
          ) : (
            <div className="tabsContainer">
              <Col md="12">
                <Tab.Container
                  defaultActiveKey="km"
                  id="uncontrolled-tab-example"
                  className="mb-3"
                >
                  <div className="nav-tabs">
                    <NavLink to={ROUTES.KILOMETERS} className="nav-link">
                      Km
                    </NavLink>
                    <NavLink to={ROUTES.EXPENSES} className="nav-link">
                      NOTA SPESE
                    </NavLink>
                  </div>
                  <Tab.Content>
                    <Routes>
                      <Route
                        path={ROUTES.KILOMETERS}
                        element={
                          <Kilometers
                            isReportConfirmed={isReportConfirmed}
                            isReadOnly={isReadOnly}
                            isAdminViewingOther={!selectedUserIsCurrentUser}
                            showModal={showModal.trip}
                            handleShowModal={handleShowModal}
                            handleHideModal={() =>
                              setShowModal(defaultModalVisibility())
                            }
                            showModalTableConfirm={showModalTableConfirm}
                            handleShowModalTableConfirm={
                              handleShowModalTableConfirm
                            }
                            handleHideModalTableConfirm={() =>
                              setShowModalTableConfirm(defaultModalVisibility())
                            }
                            month={month}
                            selectedUser={selectedUser}
                          />
                        }
                      />
                      <Route
                        path={ROUTES.EXPENSES}
                        element={
                          <Expenses
                            isReportConfirmed={isReportConfirmed}
                            isReadOnly={isReadOnly}
                            isAdminViewingOther={!selectedUserIsCurrentUser}
                            showModal={showModal.expense}
                            handleShowModal={handleShowModal}
                            handleHideModal={() =>
                              setShowModal(defaultModalVisibility())
                            }
                            showModalTableConfirm={showModalTableConfirm}
                            handleShowModalTableConfirm={
                              handleShowModalTableConfirm
                            }
                            handleHideModalTableConfirm={() =>
                              setShowModalTableConfirm(defaultModalVisibility())
                            }
                            month={month}
                            selectedUser={selectedUser}
                          />
                        }
                      />
                      <Route
                        path="*"
                        element={<Navigate to={ROUTES.TRIPS_KILOMETERS} />}
                      />
                    </Routes>
                  </Tab.Content>
                </Tab.Container>
              </Col>
            </div>
          )}
        </div>
      </div>
      <PanelTotal
        report={report}
        summaryData={summary}
        handleShowModalConfirm={handleShowModalConfirm}
        disabled={!selectedUserIsCurrentUser}
        canClose={canEditCurrentTab}
        user={selectedUserData}
      />

      <I3Modal
        show={showModalConfirm}
        onHide={handleHideModalConfirm}
        title="Chiudi mese"
      >
        <ConfirmForm
          close={handleHideModalConfirm}
          month={month}
          reloadReport={loadReport}
        />
      </I3Modal>
    </>
  );
};

export default Trips;
